import {
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  Input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UserProfileDtoV2, UsersServiceV2, UserResponseDtoV2 } from 'api-client';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { ImpersonationStore, LogService } from 'shared';

@Component({
  selector: 'app-impersonation-dropdown',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './impersonation-dropdown.component.html',
  styles: [
    `
      mark {
        background-color: #fef08a;
        color: inherit;
        padding: 0 2px;
        border-radius: 2px;
      }

      .dark mark {
        background-color: #854d0e;
      }

      .highlighted {
        outline: 2px solid var(--p-primary-500);
        outline-offset: -2px;
      }
    `,
  ],
})
export class ImpersonationDropdownComponent implements OnDestroy {
  private readonly CUSTOMER_ROLE_ID = '5525A30C-907F-4BB7-A354-A2C9FB1F32AA';

  private _usersService = inject(UsersServiceV2);
  private _impersonationStore = inject(ImpersonationStore);
  private _logService = inject(LogService);

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @Input() currentUser: UserProfileDtoV2 | null = null;
  @Output() closed = new EventEmitter<void>();

  searchText = '';

  private readonly _users = signal<UserResponseDtoV2[]>([]);
  private readonly _isLoading = signal(false);
  private readonly _hasSearched = signal(false);
  private readonly _highlightedIndex = signal<number>(-1);

  readonly users = this._users.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly highlightedIndex = this._highlightedIndex.asReadonly();
  readonly showEmptyState = computed(
    () => this._hasSearched() && !this._isLoading() && this._users().length === 0,
  );

  constructor() {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => term.length >= 3),
        switchMap((term) => {
          this._isLoading.set(true);
          // apiV2UsersGet parameters (positional):
          // firstName, lastName, email, isEmailConfirmed, isTwoFactorEnabled,
          // establishmentName, roleName, page, pageSize, sortBy, sortOrder, search
          return this._usersService
            .apiV2UsersGet(
              undefined, // firstName
              undefined, // lastName
              undefined, // email
              undefined, // isEmailConfirmed
              undefined, // isTwoFactorEnabled
              undefined, // establishmentName
              'Customer', // roleName - filter to only Customer users
              1, // page
              50, // pageSize
              undefined, // sortBy
              undefined, // sortOrder
              term, // search - now also matches establishment names
            )
            .pipe(
              map((response) => this.filterCustomerUsers(response.data ?? [])),
              catchError(() => {
                this._isLoading.set(false);
                return of([]);
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((users) => {
        this._users.set(users);
        this._isLoading.set(false);
        this._hasSearched.set(true);
      });
  }

  onSearchChange(term: string): void {
    this._highlightedIndex.set(-1);
    if (term.length < 3) {
      this._users.set([]);
      this._hasSearched.set(false);
    }
    this.searchSubject.next(term);
  }

  filterCustomerUsers(users: UserResponseDtoV2[]): UserResponseDtoV2[] {
    this._logService.debug('ImpersonationDropdownComponent', 'API returned users', {
      count: users.length,
      users,
    });

    return users.filter((user) => {
      // Exclude disabled users
      if (user.isDisabled) {
        this._logService.debug('ImpersonationDropdownComponent', 'Excluded disabled user', {
          email: user.email,
        });
        return false;
      }

      // Check if user has Customer role using ImpersonationStore
      // Convert UserResponseDtoV2 to UserProfileDtoV2 structure for canBeImpersonated check
      const userProfile: Partial<UserProfileDtoV2> = {
        id: user.id,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
        roles: user.roles?.map((r) => ({ roleId: r.roleId, name: r.name })) ?? [],
      };

      const canBeImpersonated = this._impersonationStore.canBeImpersonated(
        userProfile as UserProfileDtoV2,
      );
      this._logService.debug('ImpersonationDropdownComponent', 'User impersonation check', {
        email: user.email,
        roles: user.roles?.map((r) => `${r.name}(${r.roleId})`),
        canBeImpersonated,
      });

      return canBeImpersonated;
    });
  }

  formatUserName(user: UserResponseDtoV2): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  }

  highlightMatch(text: string, query: string): string {
    if (!query || !text) return text;

    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  focusInput(): void {
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);
  }

  onKeydown(event: KeyboardEvent): void {
    const users = this._users();
    const currentIndex = this._highlightedIndex();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (users.length === 0) return;
        // Wrap at boundary
        const nextIndex = currentIndex >= users.length - 1 ? 0 : currentIndex + 1;
        this._highlightedIndex.set(nextIndex);
        this.scrollToHighlighted();
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (users.length === 0) return;
        // Wrap at boundary
        const prevIndex = currentIndex <= 0 ? users.length - 1 : currentIndex - 1;
        this._highlightedIndex.set(prevIndex);
        this.scrollToHighlighted();
        break;

      case 'Enter':
        event.preventDefault();
        if (currentIndex >= 0 && currentIndex < users.length) {
          this.selectUser(users[currentIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        if (this.searchText.length > 0) {
          // First press: clear search
          this.searchText = '';
          this._users.set([]);
          this._hasSearched.set(false);
          this._highlightedIndex.set(-1);
        } else {
          // Second press: close dropdown
          this.closed.emit();
        }
        break;
    }
  }

  private scrollToHighlighted(): void {
    setTimeout(() => {
      const highlighted = document.querySelector('.user-item.highlighted');
      highlighted?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  selectUser(user: UserResponseDtoV2): void {
    // currentUser is passed via @Input from HeaderComponent
    if (!this.currentUser) return;

    // Brief highlight before navigation (animation via CSS)
    this._highlightedIndex.set(this._users().indexOf(user));

    // Small delay for visual feedback before page reload
    setTimeout(() => {
      // Convert UserResponseDtoV2 to UserProfileDtoV2 structure for impersonation
      const userProfile: Partial<UserProfileDtoV2> = {
        id: user.id,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
        roles: user.roles?.map((r) => ({ roleId: r.roleId, name: r.name })) ?? [],
      };

      this._impersonationStore.startImpersonation(
        userProfile as UserProfileDtoV2,
        this.currentUser!,
      );
    }, 150);
  }

  onUserClick(user: UserResponseDtoV2, index: number): void {
    this._highlightedIndex.set(index);
    this.selectUser(user);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
